import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JsBarcode from "jsbarcode";

export const generateScreeningPDF = (
  screeningData: any,
  formatDate: (date: string) => string,
  performerName: string
) => {
  if (!screeningData) {
    console.error("No screening data available");
    return null;
  }

  console.log("screeningData:", screeningData);
  console.log("pageHeaderData:", screeningData?.pageHeaderData);

  const doc = new jsPDF();

  // Add page header with logo and title
  const logoX = 45;
  const logoWidth = 20;
  const logoHeight = 20;
  const textStartX = logoX + logoWidth+5; // Start text after logo
  const headerY = 13; // or your desired Y value

  // Add logo
  if (screeningData?.pageHeaderData?.logo) {
    try {
      doc.addImage(
        screeningData.pageHeaderData.logo,
        "JPEG",
        logoX,
        headerY,
        logoWidth,
        logoHeight
      );
    } catch (error) {
      console.error("Error adding logo to PDF:", error);
    }
  }

  // Add title (not centered)
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  const titleText = screeningData?.pageHeaderData?.title?.toUpperCase() || "";
  doc.text(titleText, textStartX, headerY + 7);

  // Get the width of the title text for the line
  const titleWidth = doc.getTextWidth(titleText);

  // Add horizontal line under the title (matching title width)
  doc.setDrawColor(0);
  doc.setLineWidth(0.8);
  doc.line(textStartX, headerY + 12, textStartX + titleWidth + 2, headerY + 12);

  // Add address/phone (not centered)
  if (
    screeningData?.pageHeaderData?.address ||
    screeningData?.pageHeaderData?.phone
  ) {
    doc.setFontSize(8);
    doc.setFont("times", "bold");
    const addressText = `${screeningData?.pageHeaderData?.address ?? ""} (Ph: ${
      screeningData?.pageHeaderData?.phone ?? ""
    })`;
    doc.text(addressText, textStartX, headerY + 17);
    const addressWidth = doc.getTextWidth(addressText);
    const pageWidth = doc.internal.pageSize.width;
    const addressLineStartX = addressWidth / 1.1; // Centered horizontally
    const addressLineEndX = addressLineStartX + addressWidth - 23;

    doc.setDrawColor(0);
    doc.setLineWidth(0.1);
    doc.line(
      textStartX,
      headerY + 18,
      textStartX + addressWidth - 23,
      headerY + 18
    );
  }

  // Set up the document title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(
    "DONOR SCREENING REPORT",
    doc.internal.pageSize.width / 2,
    headerY + 28,
    { align: "center" }
  );

  // Donor information section
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");

  // Box dimensions and positioning to match screenshot exactly
  const boxWidth = 85;
  const boxHeight = 38;
  const leftBoxX = 15;
  const rightBoxX = doc.internal.pageSize.width - boxWidth - 15;
  const boxY = headerY + 35;

  // Left box - Donor details
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.roundedRect(leftBoxX, boxY, boxWidth, boxHeight, 2, 2, "S");

  // Vertical line in left box
  const leftVerticalLineX = leftBoxX + 32;
  doc.line(
    leftVerticalLineX,
    boxY + 3,
    leftVerticalLineX,
    boxY - 3 + boxHeight
  );

  const lineSpacing = 5;
  let currentY = boxY + 10;

  // Left box content with labels right-aligned near vertical line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  // Labels positioned just before the vertical line, right-aligned
  const labelX = leftVerticalLineX - 2; // Position labels right before the vertical line
  const valueX = leftVerticalLineX + 2; // Values start just after vertical line

  doc.text("DONOR ID", labelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(String(screeningData.donorId ?? "N/A"), valueX, currentY);

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.text("DONOR NAME", labelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(String(screeningData.donorData?.name ?? "N/A"), valueX, currentY);

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11); // Corrected font size for consistency
  doc.text("S/O", labelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(
    String(screeningData.donorData?.fatherName ?? "N/A"),
    valueX,
    currentY
  );

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.text("AGE / GENDER", labelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(
    `${String(screeningData.donorData?.age ?? "N/A")} YEARS | ${String(
      screeningData.donorData?.gender.toUpperCase() ?? "N/A"
    )}`,
    valueX,
    currentY
  );

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.text("CONTACT NO", labelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(String(screeningData.donorData?.cellNo ?? "N/A"), valueX, currentY);

  // Right box - Blood group and donation details
  doc.roundedRect(rightBoxX, boxY, boxWidth, boxHeight, 2, 2, "S");

  // Vertical line in right box
  const rightVerticalLineX = rightBoxX + 38;
  doc.line(rightVerticalLineX, boxY + 3, rightVerticalLineX, boxY + 25); // Shorter line to accommodate barcode

  currentY = boxY + 10;

  // Right box content with labels right-aligned near vertical line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11); // Adjusted for consistency
  const rightLabelX = rightVerticalLineX - 2; // Position labels right before the vertical line
  const rightValueX = rightVerticalLineX + 2; // Values start just after vertical line

  doc.text("BLOOD GROUP", rightLabelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(String(screeningData.bloodGroup ?? "N/A"), rightValueX, currentY);

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.text("RH", rightLabelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(
    screeningData.rhFactor === "+" ? "POSITIVE" : "NEGATIVE",
    rightValueX,
    currentY
  );

  currentY += lineSpacing;
  doc.setFont("helvetica", "normal");
  doc.text("DONATION DATE", rightLabelX, currentY, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(
    String(formatDate(screeningData.createdAt) ?? "N/A"),
    rightValueX,
    currentY
  );

  // Generate barcode with donor ID
  const generateBarcode = (text: string) => {
    const canvas = document.createElement("canvas");
    try {
      JsBarcode(canvas, text, {
        format: "CODE128",
        width: 1.5,
        height: 10,
        displayValue: false,
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error generating barcode:", error);
      return null;
    }
  };

  // Add barcode to right box - centered horizontally
  const barcodeData = generateBarcode(
    String(screeningData.donorId ?? "2500419")
  );
  if (barcodeData) {
    const barcodeX = rightBoxX + (boxWidth - 45) / 2; // Center the barcode horizontally
    const barcodeY = boxY + 27;
    const barcodeWidth = 35;
    const barcodeHeight = 8;

    try {
      doc.addImage(
        barcodeData,
        "PNG",
        barcodeX,
        barcodeY,
        barcodeWidth,
        barcodeHeight
      );
    } catch (error) {
      console.error("Error adding barcode to PDF:", error);
    }
  }

  // Test results table
  const startY = boxY + boxHeight + 8;

  // First table - HBsAg, HCV, HIV
  autoTable(doc, {
    startY: startY,
    head: [[
      "TEST NAME",
      "CLIA/ELISA Method",
      "Cut Off",
      "ICT Screening",
    ]],
    body: [
      [
        "Hepatitis B Surface Antigen ( HBsAg )",
        String(screeningData.hbsAg?.value ?? "N/A"),
        String(screeningData.hbsAg?.cutoff ?? "N/A"),
        String(screeningData.hbsAg?.result ?? "N/A"),
      ],
      [
        "Hepatitis C Virus Antibody( HCV )",
        String(screeningData.hcv?.value ?? "N/A"),
        String(screeningData.hcv?.cutoff ?? "N/A"),
        String(screeningData.hcv?.result ?? "N/A"),
      ],
      [
        "Human Immunodeficiency Virus( HIV 1 & 2 )",
        String(screeningData.hiv?.value ?? "N/A"),
        String(screeningData.hiv?.cutoff ?? "N/A"),
        String(screeningData.hiv?.result ?? "N/A"),
      ],
      [
        "Syphilis(VDRL)",
        String(
          typeof screeningData.vdrl === "object"
            ? screeningData.vdrl?.value ?? 0
            : 0
        ),
        String(
          typeof screeningData.vdrl === "object"
            ? screeningData.vdrl?.cutoff ?? 1
            : 1
        ),
        String(
          typeof screeningData.vdrl === "object"
            ? screeningData.vdrl?.result ?? "N/A"
            : screeningData.vdrl ?? "N/A"
        ),
      ],
      [
        "Malarial Parasite (MP)",
        "-",
        "-",
        String(
          typeof screeningData.mp === "object"
            ? screeningData.mp?.result ?? "N/A"
            : screeningData.mp ?? "N/A"
        ),
      ],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
  });

  // Note about test method
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  const firstTableFinalY = doc.lastAutoTable?.finalY ?? startY + 30;
  const methodText =
    String(screeningData?.bloodBankConfig?.screeningMethodText || "").trim() ||
    "Tests have been performed on fully automatic analyser Vitros ECI by CLIA method.";
  const wrappedMethodText = doc.splitTextToSize(methodText, 180);
  doc.text(wrappedMethodText, 15, firstTableFinalY + 5);
  const methodBlockHeight = Array.isArray(wrappedMethodText)
    ? wrappedMethodText.length * 4
    : 4;

  // CBC test
  autoTable(doc, {
    startY: firstTableFinalY + 8 + methodBlockHeight,
    head: [["CBC TEST", "RESULT", "UNIT"]],
    body: [
      // ["CBC", "HGB, WPC, PLT"],
      ["WBC", String(screeningData.wbc ?? "-"), "10^9/L"],
      ["HGB", String(screeningData.hgb ?? "-"), "g/dL"],
      ["PLT", String(screeningData.plt ?? "-"), "10^9/L"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
  });

  // Note about CBC method
  const thirdTableFinalY = doc.lastAutoTable?.finalY ?? firstTableFinalY + 40;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(
    "Test has been performed on automated hematology analyser.",
    15,
    thirdTableFinalY + 5
  );

  const pageWidth = doc.internal.pageSize.width;
  const bottomY = doc.internal.pageSize.height - 20;
  const signLabelY = bottomY - 48;
  const signBoxY = signLabelY + 2;
  const signBoxW = 72;
  const signBoxH = 24;
  const leftSignX = 18;
  const rightSignX = pageWidth - signBoxW - 18;

  const drawSignature = (src: unknown, x: number, y: number) => {
    const data = String(src ?? "");
    if (!data.startsWith("data:image/")) return;
    const format = /image\/jpe?g/i.test(data) ? "JPEG" : "PNG";
    try {
      doc.addImage(data, format, x + 2, y + 1, signBoxW - 4, signBoxH - 2);
    } catch (error) {
      console.error("Error adding signature to PDF:", error);
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Technologist Signature/Thumb", leftSignX, signLabelY);
  doc.text("Doctor's Signature / Thumb", rightSignX, signLabelY);
  doc.setDrawColor(0);
  doc.setLineWidth(0.4);
  doc.rect(leftSignX, signBoxY, signBoxW, signBoxH);
  doc.rect(rightSignX, signBoxY, signBoxW, signBoxH);
  drawSignature(screeningData.technologistSignature, leftSignX, signBoxY);
  drawSignature(screeningData.doctorSignature, rightSignX, signBoxY);

  doc.setLineWidth(0.5);
  doc.line(15, bottomY - 10, pageWidth - 15, bottomY - 10);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(
    "This is a computer generated report, duly verified by Pathologist, thus it requires no signature or stamp.",
    doc.internal.pageSize.width / 2,
    bottomY - 3,
    { align: "center" }
  );

  return doc;
};
